/**
 * Write down that somebody was contacted about one requirement.
 *
 * IT DOCUMENTS; IT DOES NOT SEND. Nothing here dispatches a mail, and the title
 * says "Kontakt dokumentieren" rather than "E-Mail senden" for that reason. The
 * row it creates is a record of something that already happened — usually
 * outside this system entirely, in a phone call or at a front door.
 *
 * THE CHANNEL RULE IS ASYMMETRIC AND THE ASYMMETRY IS THE PRODUCT.
 * "We asked" is incomplete without "how", so a request or a reminder requires a
 * channel. A note does NOT: writing down that the tenant promised the papers is
 * complete on its own, and it is equally valid to say the promise came by
 * phone. So a note may carry any channel or none, and the form must not narrow
 * what the backend accepts — a frontend that forbade `note + phone` would make
 * a legitimate record impossible to file, with no error anywhere to explain it.
 *
 * A NOTE NEVER MOVES "ZULETZT ANGEFRAGT", whatever channel it carries. That is
 * the backend's rule (`last_requested_at` aggregates request and reminder
 * only), and this form neither implements nor restates it: it sends `kind` and
 * reads the consequence back from the read model.
 *
 * THE CLOCK HERE IS AN INPUT DEFAULT, NOT A DERIVATION. `occurred_at` starts at
 * now and refuses the future, because a contact that has not happened yet
 * cannot be documented. Both are properties of a timestamp field. Nothing on
 * this surface reads a clock to decide a requirement's status, deadline or
 * evidence — that remains the engine's, and the guard suite pins the boundary.
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { createRequirementCommunication } from "../../../api/adminData/tenancyRequirements";
import {
  businessLocalToIso,
  instantToBusinessLocalInput,
} from "../../../utils/datetime";
import { requirementTypeLabel } from "./requirementLabels";

/**
 * The two vocabularies, in German, with the wire code each one sends.
 *
 * Spelled out here rather than in `requirementLabels.js` because that module is
 * the REQUIREMENT vocabulary — three types and five statuses — and these are the
 * communication one. They travel with the only surface that speaks them.
 */
export const CONTACT_KIND_OPTIONS = [
  { value: "request", label: "Nachweis angefragt" },
  { value: "reminder", label: "Erinnerung" },
  { value: "note", label: "Notiz" },
];

export const CONTACT_CHANNEL_OPTIONS = [
  { value: "email", label: "E-Mail" },
  { value: "phone", label: "Telefon" },
  { value: "in_person", label: "Persönlich" },
  { value: "other", label: "Sonstiges" },
];

/** Request and reminder are the two that mean "we asked". */
const CHANNEL_REQUIRED_KINDS = ["request", "reminder"];

const FIELD =
  "w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus:ring-1 focus:ring-ring disabled:opacity-50";
const LABEL = "text-[12px] font-medium text-muted-foreground";

/**
 * @param {{
 *   row: object|null,
 *   onClose: () => void,
 *   onCreated: () => void,
 * }} props
 */
export default function RequirementContactModal({ row, onClose, onCreated }) {
  const [kind, setKind] = useState("request");
  const [channel, setChannel] = useState("email");
  const [occurredLocal, setOccurredLocal] = useState("");
  const [subject, setSubject] = useState("");
  const [note, setNote] = useState("");
  const [pending, setPending] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  // Reopening starts a fresh record rather than resuming the last one: the
  // previous contact was filed, and its subject line is not a draft.
  useEffect(() => {
    if (!row) return;
    setKind("request");
    setChannel("email");
    setOccurredLocal(instantToBusinessLocalInput(Date.now()));
    setSubject("");
    setNote("");
    setSubmitError(null);
    setPending(false);
  }, [row]);

  const channelRequired = CHANNEL_REQUIRED_KINDS.includes(kind);

  /**
   * A moment that has not arrived cannot have been documented.
   *
   * The backend refuses it too, against one pinned instant, and stays the
   * authority; checking here only spares the operator a round trip and a red
   * error for something the form could have said immediately.
   */
  const isFuture = useMemo(() => {
    if (!occurredLocal) return false;
    const iso = businessLocalToIso(occurredLocal);
    if (!iso) return false;
    return new Date(iso).getTime() > Date.now();
  }, [occurredLocal]);

  if (!row) return null;

  const blocked = pending || isFuture || (channelRequired && !channel) || !occurredLocal;

  async function submit() {
    setPending(true);
    setSubmitError(null);
    try {
      await createRequirementCommunication(row.tenancy_id, row.requirement_type, {
        kind,
        // A note with no channel sends no channel at all — see the header.
        channel: channel || undefined,
        occurred_at: businessLocalToIso(occurredLocal) || undefined,
        subject: subject.trim() || undefined,
        note: note.trim() || undefined,
      });
      toast.success("Kontakt dokumentiert.");
      onCreated();
    } catch (e) {
      const message = e?.message || "Der Kontakt konnte nicht gespeichert werden.";
      setSubmitError(message);
      toast.error(message);
      setPending(false);
    }
  }

  return (
    <AlertDialog open onOpenChange={(open) => (!open && !pending ? onClose() : null)}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>Kontakt dokumentieren</AlertDialogTitle>
          <AlertDialogDescription>
            Hält fest, dass zu «{requirementTypeLabel(row.requirement_type)}»
            Kontakt bestand. Es wird nichts versendet.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <label htmlFor="requirement-contact-kind" className={LABEL}>
              Art
            </label>
            <select
              id="requirement-contact-kind"
              value={kind}
              disabled={pending}
              onChange={(e) => setKind(e.target.value)}
              className={FIELD}
            >
              {CONTACT_KIND_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <label htmlFor="requirement-contact-channel" className={LABEL}>
              {channelRequired ? "Kanal" : "Kanal (optional)"}
            </label>
            <select
              id="requirement-contact-channel"
              value={channel}
              disabled={pending}
              onChange={(e) => setChannel(e.target.value)}
              className={FIELD}
            >
              {/* Offered only where it is legal. A note may be filed without a
                  channel; a request may not, so the empty choice would be a
                  dead end the server rejects. */}
              {channelRequired ? null : <option value="">Kein Kanal</option>}
              {CONTACT_CHANNEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="grid gap-1.5">
            <label htmlFor="requirement-contact-occurred" className={LABEL}>
              Zeitpunkt
            </label>
            <input
              id="requirement-contact-occurred"
              type="datetime-local"
              value={occurredLocal}
              disabled={pending}
              onChange={(e) => setOccurredLocal(e.target.value)}
              className={FIELD}
            />
            {isFuture ? (
              <p
                role="alert"
                data-testid="requirement-contact-future"
                className="m-0 text-[12px] text-destructive"
              >
                Der Zeitpunkt darf nicht in der Zukunft liegen.
              </p>
            ) : (
              <p className="m-0 text-[11.5px] text-muted-foreground">
                Nachträgliche Dokumentation ist möglich.
              </p>
            )}
          </div>

          {/* A subject belongs to a mail. Showing the field for a phone call
              would invite an answer to a question that was never asked. */}
          {channel === "email" ? (
            <div className="grid gap-1.5">
              <label htmlFor="requirement-contact-subject" className={LABEL}>
                Betreff
              </label>
              <input
                id="requirement-contact-subject"
                type="text"
                maxLength={300}
                value={subject}
                disabled={pending}
                onChange={(e) => setSubject(e.target.value)}
                className={FIELD}
              />
            </div>
          ) : null}

          <div className="grid gap-1.5">
            <label htmlFor="requirement-contact-note" className={LABEL}>
              Notiz
            </label>
            {/* Short on purpose. This is a record of WHAT happened, not a copy
                of what was written — a full mail body here would put tenant
                correspondence into a field nobody expects to hold it. */}
            <textarea
              id="requirement-contact-note"
              rows={3}
              value={note}
              disabled={pending}
              onChange={(e) => setNote(e.target.value)}
              className={FIELD}
            />
          </div>

          {submitError ? (
            <p
              role="alert"
              className="m-0 rounded-[8px] border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12.5px] text-destructive"
            >
              {submitError}
            </p>
          ) : null}
        </div>

        <AlertDialogFooter className="gap-2">
          <button
            type="button"
            disabled={pending}
            onClick={onClose}
            className="inline-flex h-10 items-center justify-center rounded-md border border-border px-4 py-2 text-sm font-medium text-foreground hover:bg-muted disabled:pointer-events-none disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            disabled={blocked}
            onClick={submit}
            className="inline-flex h-10 items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:pointer-events-none disabled:opacity-50"
          >
            {pending ? "Speichere …" : "Kontakt speichern"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
